import React, { useRef, useEffect } from 'react';
import { Table, Form, Dropdown } from 'react-bootstrap';
import { AppButton } from '../ui';
import Shimmer from "./Shimmer";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Common DataTable Component
 * 
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Array of column definitions: 
 *   { key: string, label: string, render: (row, index) => ReactNode, sortable: boolean, width: string }
 * @param {Object} pagination - { page, pageSize, total, onPageChange }
 * @param {Object} filters - { searchValue, onSearchChange, customFilters }
 * @param {Array} bulkActions - Array of bulk action buttons
 * @param {Function} onRowClick - Callback for row click
 * @param {Function} onRowContextMenu - (row, event) => void, callback for row right-click
 * @param {Function} rowClassName - (row) => string, optional per-row CSS class
 * @param {Boolean} selectable - Show checkboxes for selection
 * @param {Array} selectedRows - Array of selected row IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {String} emptyMessage - Message to show when no data
 * @param {Boolean} loading - Show loading state
 */
const DataTable = ({
  data = [],
  columns = [],
  pagination = null,
  filters = null,
  bulkActions = null,
  onRowClick = null,
  onRowContextMenu = null,
  rowClassName = null,
  selectable = false,
  selectedRows = [],
  onSelectionChange = () => {},
  emptyMessage = "No data found",
  loading = false,
  hoverable = true,
  striped = false,
  bordered = false,
  renderExpandedRow = null,
}) => {
  const toRowId = (row, index) => {
    const raw = row.id ?? row._id;
    if (raw == null) return index;
    if (typeof raw === 'object') return JSON.stringify(raw);
    return raw;
  };

  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const allIds = data.map((row, index) => toRowId(row, index));
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleRowCheck = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedRows, id]);
    } else {
      onSelectionChange(selectedRows.filter(rowId => rowId !== id));
    }
  };

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;
  const currentPage = pagination?.page || 1;
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!selectable || !selectAllRef.current) return;
    const el = selectAllRef.current;
    const n = data.length;
    const k = selectedRows.length;
    el.indeterminate = n > 0 && k > 0 && k < n;
  }, [selectable, data.length, selectedRows.length]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="data-table-wrapper">
      {/* Filters Section */}
      {filters && (
        <div className="data-table-filters">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {/* Search */}
            {filters.searchPlaceholder && (
              <div className="search-input-wrapper">
                <Search size="1em" className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder={filters.searchPlaceholder}
                  value={filters.searchValue || ''}
                  onChange={(e) => filters.onSearchChange?.(e.target.value)}
                  className="search-input"
                />
              </div>
            )}

            {/* Custom Filter Dropdowns */}
            {filters.customFilters?.map((filter, index) => (
              <Dropdown key={index}>
                <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown">
                  {filter.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {filter.options?.map((option, idx) => (
                    <Dropdown.Item 
                      key={idx}
                      onClick={() => filter.onChange?.(option.value)}
                      active={filter.value === option.value}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ))}
          </div>

            {/* Bulk Actions */}
          {bulkActions && selectedRows.length > 0 && (
            <div className="bulk-actions">
              {bulkActions.map((action, index) => {
                const bsVariantMap = { primary: 'default', danger: 'destructive', secondary: 'secondary', warning: 'secondary', success: 'default', dark: 'default', light: 'ghost', link: 'ghost' };
                return (
                  <AppButton
                    key={index}
                    variant={bsVariantMap[action.variant] || 'default'}
                    size="sm"
                    onClick={() => action.onClick?.(selectedRows)}
                  >
                    {action.icon && <span className="me-2">{action.icon}</span>}
                    {action.label}
                  </AppButton>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table Section */}
      <div className="data-table-container">
        <Table 
          hover={hoverable} 
          striped={striped}
          bordered={bordered}
          responsive 
          className="data-table mb-0"
        >
          <thead>
            <tr>
              {selectable && (
                <th className="data-table-select-th">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="app-table-checkbox"
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleCheckAll}
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {columns.map((col, index) => (
                <th
                  key={index}
                  style={{ width: col.width }}
                  className={[
                    col.sortable ? 'sortable' : '',
                    col.align ? `text-${col.align}` : '',
                    col.className || '',
                    col.headClassName || '',
                  ].filter(Boolean).join(' ')}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              /*
                Skeleton rows, not a spinner. Project convention is Shimmer, and a
                table is the case it was made for: the row count and column widths
                are already known, so the page does not reflow when the data lands.
                Rendered as real <tr>/<td> so the browser keeps the column geometry.
              */
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`dt-skeleton-${i}`}>
                  {selectable && <td><Shimmer height={16} /></td>}
                  {columns.map((col, c) => (
                    <td key={c}><Shimmer height={16} /></td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-5 text-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowId = toRowId(row, rowIndex);
                const isSelected = selectedRows.includes(rowId);
                
                return (
                  <React.Fragment key={rowId}>
                  <tr
                    onClick={() => onRowClick?.(row)}
                    onContextMenu={onRowContextMenu ? (e) => onRowContextMenu(row, e) : undefined}
                    className={[
                      onRowClick ? 'cursor-pointer' : '',
                      typeof rowClassName === 'function' ? rowClassName(row) : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {selectable && (
                      <td className="data-table-select-td" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="app-table-checkbox"
                          checked={isSelected}
                          onChange={(e) => handleRowCheck(rowId, e.target.checked)}
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        className={[
                          col.align ? `text-${col.align}` : '',
                          col.className || '',
                        ].filter(Boolean).join(' ') || undefined}
                      >
                        {col.render ? col.render(row, rowIndex) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {renderExpandedRow && renderExpandedRow(row, rowIndex)}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Section */}
      {pagination && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            {pagination.total === 0 ? (
              <span>No entries</span>
            ) : (
              <span>
                Showing {((currentPage - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(currentPage * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} entries
              </span>
            )}
            {pagination.onPageSizeChange && (
              <select
                className="data-table-page-size"
                value={pagination.pageSize}
                onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {(pagination.pageSizeOptions || [10, 25, 50, 100]).map((size) => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            )}
          </div>

          <div className="pagination-controls">
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
              className="me-2"
            >
              <ChevronLeft size={12} />
            </AppButton>
            
            {getPageNumbers().map((pageNum, index) => (
              pageNum === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <AppButton
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => pagination.onPageChange?.(pageNum)}
                  className="me-2"
                >
                  {pageNum}
                </AppButton>
              )
            ))}
            
            <AppButton
              variant="ghost"
              size="sm"
              onClick={() => pagination.onPageChange?.(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={12} />
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
